import {
  Timestamp,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "../firebase/app";
import {
  ACTION_AUDIT_WINDOW_MS,
  actionAuditDocumentID,
  actionAuditEventFirestoreData,
  actionAuditEventIsExpired,
  actionAuditFieldChanges,
  actionAuditReferenceDate,
  actionAuditValueFirestoreData,
  flattenActionAuditData,
  humanActionAuditActor,
  isActionAuditFieldUndoable,
  isEqualActionAuditFirestoreValue,
  parseActionAuditEvent,
  type ActionAuditContext,
  type ActionAuditEvent,
  type ActionAuditFieldChange,
  type ActionAuditScope,
} from "../models/actionAudit";
import { isMembershipCurrentlyActive, resolvedLeagueID, type SBotMembership, type SBotUser } from "../models/user";

// Stable service-level facade used by routes and mutation seams.
export {
  actionAuditEventIsExpired,
  actionAuditReferenceDate,
  actionAuditValueDisplayString,
  aiAssistantActionAuditActor,
  externalAgentActionAuditActor,
  humanActionAuditActor,
  isActionAuditFieldUndoable,
} from "../models/actionAudit";
export { actionAuditScopes } from "../models/featureAccessPolicy";

export const ACTION_AUDIT_COLLECTION = "actionAuditEvents";
export const ACTION_AUDIT_FETCH_LIMIT = 200;

export interface ActionAuditReader {
  fetchLeagueEvents(leagueID: string, now: Date): Promise<ActionAuditEvent[]>;
  fetchOrganizationEvents(leagueID: string, organizationID: string, now: Date): Promise<ActionAuditEvent[]>;
  fetchSubjectEvents(subjectUserID: string, now: Date): Promise<ActionAuditEvent[]>;
  fetchEvent(id: string): Promise<ActionAuditEvent | null>;
  fetchRawDocument(collectionName: string, documentID: string): Promise<Record<string, unknown> | null>;
}

export interface ActionAuditUndoWriter {
  restoreField(
    collectionName: string,
    documentID: string,
    fieldKey: string,
    value: unknown,
    stamps: Record<string, unknown>,
  ): Promise<void>;
}

export type ActionAuditRecorder = (
  input: { before?: Record<string, unknown> | null; after?: Record<string, unknown> | null; context: ActionAuditContext },
) => void;

function parseSnapshotDocuments(
  documents: Array<{ id: string; data(): DocumentData }>,
): ActionAuditEvent[] {
  return documents.flatMap((snapshot) => {
    const event = parseActionAuditEvent(snapshot.id, snapshot.data());
    return event ? [event] : [];
  });
}

async function queryEvents(...constraints: Parameters<typeof query>[1][]): Promise<ActionAuditEvent[]> {
  const snapshot = await getDocs(query(collection(db, ACTION_AUDIT_COLLECTION), ...constraints));
  return parseSnapshotDocuments(snapshot.docs);
}

export const firestoreActionAuditReader: ActionAuditReader = {
  async fetchLeagueEvents(leagueID, now) {
    return queryEvents(
      where("leagueID", "==", leagueID),
      where("expiresAt", ">", Timestamp.fromDate(now)),
      orderBy("expiresAt", "desc"),
      limit(ACTION_AUDIT_FETCH_LIMIT),
    );
  },

  async fetchOrganizationEvents(leagueID, organizationID, now) {
    return queryEvents(
      where("leagueID", "==", leagueID),
      where("organizationID", "==", organizationID),
      where("expiresAt", ">", Timestamp.fromDate(now)),
      orderBy("expiresAt", "desc"),
      limit(ACTION_AUDIT_FETCH_LIMIT),
    );
  },

  async fetchSubjectEvents(subjectUserID, now) {
    return queryEvents(
      where("subjectUserID", "==", subjectUserID),
      where("expiresAt", ">", Timestamp.fromDate(now)),
      orderBy("expiresAt", "desc"),
      limit(ACTION_AUDIT_FETCH_LIMIT),
    );
  },

  async fetchEvent(id) {
    const snapshot = await getDoc(doc(db, ACTION_AUDIT_COLLECTION, id));
    return snapshot.exists() ? parseActionAuditEvent(snapshot.id, snapshot.data()) : null;
  },

  async fetchRawDocument(collectionName, documentID) {
    const snapshot = await getDoc(doc(db, collectionName, documentID));
    return snapshot.exists() ? snapshot.data() : null;
  },
};

export const firestoreActionAuditUndoWriter: ActionAuditUndoWriter = {
  async restoreField(collectionName, documentID, fieldKey, value, stamps) {
    await updateDoc(doc(db, collectionName, documentID), {
      [fieldKey]: value === undefined ? deleteField() : value,
      ...stamps,
    });
  },
};

export function makeActionAuditEvent({
  before,
  after,
  context,
  now = new Date(),
  idSeed = crypto.randomUUID().toLowerCase(),
}: {
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  context: ActionAuditContext;
  now?: Date;
  idSeed?: string;
}): ActionAuditEvent | null {
  const fieldChanges = after
    ? actionAuditFieldChanges(before, after, context.fieldLabels)
    : [];
  if (context.actionType === "update" && fieldChanges.length === 0) return null;
  return {
    id: actionAuditDocumentID(context.actor.userID, idSeed),
    leagueID: context.leagueID,
    organizationID: context.organizationID,
    subjectUserID: context.subjectUserID,
    targetCollection: context.targetCollection,
    targetDocumentID: context.targetDocumentID,
    targetDisplayName: context.targetDisplayName,
    actionType: context.actionType,
    actorUserID: context.actor.userID,
    actorDisplayName: context.actor.displayName,
    actorKind: context.actor.kind,
    toolActionType: context.actor.toolActionType,
    fieldChanges,
    reversesEventID: context.reversesEventID,
    reversesFieldKey: context.reversesFieldKey,
    clientTimestamp: now,
    expiresAt: new Date(now.getTime() + ACTION_AUDIT_WINDOW_MS),
  };
}

/**
 * Fire-and-forget by contract. Event assembly and write failures are both
 * swallowed so a primary edit never waits for or fails because of auditing.
 */
export function recordActionAudit(
  input: {
    before?: Record<string, unknown> | null;
    after?: Record<string, unknown> | null;
    context: ActionAuditContext;
  },
  write: (event: ActionAuditEvent) => Promise<void> = async (event) => {
    await setDoc(doc(db, ACTION_AUDIT_COLLECTION, event.id), actionAuditEventFirestoreData(event));
  },
): void {
  try {
    const event = makeActionAuditEvent(input);
    if (!event) return;
    void Promise.resolve(write(event)).catch(() => undefined);
  } catch {
    // Audit recording must not affect the primary write.
  }
}

export function mergedActionAuditEvents(batches: ActionAuditEvent[][]): ActionAuditEvent[] {
  const byID = new Map<string, ActionAuditEvent>();
  for (const event of batches.flat()) {
    if (!byID.has(event.id)) byID.set(event.id, event);
  }
  return [...byID.values()].sort(
    (first, second) => actionAuditReferenceDate(second).getTime() - actionAuditReferenceDate(first).getTime(),
  );
}

export async function loadActionAuditEvents(
  scopes: ActionAuditScope[],
  reader: ActionAuditReader = firestoreActionAuditReader,
  now = new Date(),
): Promise<ActionAuditEvent[]> {
  const batches = await Promise.all(scopes.flatMap((scope): Array<Promise<ActionAuditEvent[]>> => {
    if (scope.kind === "league") return [reader.fetchLeagueEvents(scope.leagueID, now)];
    if (scope.kind === "organizations") {
      return scope.organizationIDs.map((organizationID) =>
        reader.fetchOrganizationEvents(scope.leagueID, organizationID, now));
    }
    return [reader.fetchSubjectEvents(scope.userID, now)];
  }));
  return mergedActionAuditEvents(batches);
}

export async function fetchActionAuditEvent(
  id: string,
  reader: ActionAuditReader = firestoreActionAuditReader,
): Promise<ActionAuditEvent | null> {
  return reader.fetchEvent(id);
}

export const loadActionAuditEvent = fetchActionAuditEvent;

export function eventMatchesActionAuditScopes(event: ActionAuditEvent, scopes: ActionAuditScope[]): boolean {
  return scopes.some((scope) => {
    if (scope.kind === "league") return event.leagueID === scope.leagueID;
    if (scope.kind === "organizations") {
      return event.leagueID === scope.leagueID
        && !!event.organizationID
        && scope.organizationIDs.includes(event.organizationID);
    }
    return event.subjectUserID === scope.userID;
  });
}

export function filterActionAuditEvents(events: ActionAuditEvent[], scope: ActionAuditScope): ActionAuditEvent[] {
  return events.filter((event) => eventMatchesActionAuditScopes(event, [scope]));
}

export function actionAuditScopeTitle(scope: ActionAuditScope): string {
  switch (scope.kind) {
    case "league":
      return "League";
    case "organizations":
      return "Organizations";
    case "athleteSelf":
      return "My Activity";
  }
}

export function undoActionAuditEventsByReversedID(
  events: ActionAuditEvent[],
): Record<string, ActionAuditEvent[]> {
  return events.reduce<Record<string, ActionAuditEvent[]>>((result, event) => {
    if (event.actionType === "undo" && event.reversesEventID) {
      (result[event.reversesEventID] ??= []).push(event);
    }
    return result;
  }, {});
}

function activeLeagueMemberships(user: SBotUser, leagueID: string): SBotMembership[] {
  return user.memberships.filter(
    (membership) => isMembershipCurrentlyActive(membership) && resolvedLeagueID(membership) === leagueID,
  );
}

export function canUndoActionAuditEvent(
  event: ActionAuditEvent,
  currentUser: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
): boolean {
  if (isSBotLabsAdmin) return true;
  if (!currentUser) return false;
  const memberships = activeLeagueMemberships(currentUser, event.leagueID);
  const isLeagueAdmin = memberships.some(
    (membership) => membership.targetType === "league" && membership.role === "admin",
  );
  const isLeagueManagement = memberships.some(
    (membership) => membership.targetType === "league"
      && (membership.role === "admin" || membership.role === "leagueManager"),
  );
  const isOrganizationAdmin = !!event.organizationID && memberships.some(
    (membership) => membership.targetType === "organization"
      && membership.role === "admin"
      && (membership.organizationID ?? membership.targetID) === event.organizationID,
  );
  const isSubjectSelf = event.subjectUserID === currentUser.id;

  switch (event.targetCollection) {
    case "leagues":
    case "leagueAthleteSchemas":
    case "leagueOrganizationAdminSchemas":
      return isLeagueAdmin;
    case "leagueCompetitionTemplates":
    case "leagueCompetitionInstances":
      return isLeagueManagement;
    case "leagueAthleteValues":
      return isLeagueManagement || isOrganizationAdmin || isSubjectSelf;
    case "leagueOrganizationAdminValues":
      return isLeagueAdmin || isOrganizationAdmin || isSubjectSelf;
    case "organizations":
      return isLeagueAdmin || isOrganizationAdmin;
    default:
      return false;
  }
}

export type ActionAuditUndoErrorCode =
  | "expired"
  | "notUndoable"
  | "conflict"
  | "notPermitted"
  | "targetMissing"
  | "unsupportedTarget";

const UNDO_ERROR_MESSAGES: Record<ActionAuditUndoErrorCode, string> = {
  expired: "This change is older than 24 hours and can no longer be undone.",
  notUndoable: "This change cannot be restored automatically.",
  conflict: "This field was changed again after this event. Undo the newer change first.",
  notPermitted: "You don't have permission to undo this change.",
  targetMissing: "The edited record no longer exists.",
  unsupportedTarget: "Undo isn't supported for this kind of change.",
};

export class ActionAuditUndoError extends Error {
  constructor(public readonly code: ActionAuditUndoErrorCode) {
    super(UNDO_ERROR_MESSAGES[code]);
    this.name = "ActionAuditUndoError";
  }
}

const SUPPORTED_UNDO_COLLECTIONS = new Set([
  "leagues",
  "organizations",
  "leagueAthleteValues",
  "leagueAthleteSchemas",
  "leagueOrganizationAdminSchemas",
  "leagueOrganizationAdminValues",
  "leagueCompetitionTemplates",
  "leagueCompetitionInstances",
]);

const UPDATED_BY_SUPPORTED_COLLECTIONS = new Set([
  "leagueAthleteValues",
  "leagueAthleteSchemas",
  "leagueOrganizationAdminSchemas",
  "leagueCompetitionTemplates",
  "leagueCompetitionInstances",
]);

export function actionAuditUndoStamps(
  collectionName: string,
  userID: string,
  timestamp: Date,
): Record<string, unknown> {
  return {
    updatedAt: Timestamp.fromDate(timestamp),
    ...(UPDATED_BY_SUPPORTED_COLLECTIONS.has(collectionName) ? { updatedByUserID: userID } : {}),
  };
}

export function applyActionAuditValue(
  data: Record<string, unknown>,
  fieldKey: string,
  value: unknown,
): Record<string, unknown> {
  const result = { ...data };
  const components = fieldKey.split(".");
  const leaf = components.pop();
  if (!leaf) return result;
  let cursor = result;
  for (const component of components) {
    const nested = cursor[component];
    const copy = nested && typeof nested === "object" && !Array.isArray(nested)
      ? { ...(nested as Record<string, unknown>) }
      : {};
    cursor[component] = copy;
    cursor = copy;
  }
  if (value === undefined) delete cursor[leaf];
  else cursor[leaf] = value;
  return result;
}

export function matchesActionAuditRecordedNewValue(
  current: unknown,
  change: ActionAuditFieldChange,
): boolean {
  return isEqualActionAuditFirestoreValue(
    current,
    change.newValue ? actionAuditValueFirestoreData(change.newValue) : undefined,
  );
}

export async function undoActionAuditField(
  {
    event,
    change,
    currentUser,
    isSBotLabsAdmin,
  }: {
    event: ActionAuditEvent;
    change: ActionAuditFieldChange;
    currentUser: SBotUser;
    isSBotLabsAdmin: boolean;
  },
  dependencies: {
    reader?: ActionAuditReader;
    writer?: ActionAuditUndoWriter;
    recorder?: ActionAuditRecorder;
    now?: () => Date;
  } = {},
): Promise<void> {
  if (!canUndoActionAuditEvent(event, currentUser, isSBotLabsAdmin)) {
    throw new ActionAuditUndoError("notPermitted");
  }
  if (!isActionAuditFieldUndoable(change)) throw new ActionAuditUndoError("notUndoable");
  const timestamp = dependencies.now?.() ?? new Date();
  if (actionAuditEventIsExpired(event, timestamp)) throw new ActionAuditUndoError("expired");
  if (!SUPPORTED_UNDO_COLLECTIONS.has(event.targetCollection)) {
    throw new ActionAuditUndoError("unsupportedTarget");
  }

  const reader = dependencies.reader ?? firestoreActionAuditReader;
  const currentData = await reader.fetchRawDocument(event.targetCollection, event.targetDocumentID);
  if (!currentData) throw new ActionAuditUndoError("targetMissing");
  const flatCurrent = flattenActionAuditData(currentData);
  if (!matchesActionAuditRecordedNewValue(flatCurrent[change.key], change)) {
    throw new ActionAuditUndoError("conflict");
  }

  const oldValue = change.oldValue ? actionAuditValueFirestoreData(change.oldValue) : undefined;
  await (dependencies.writer ?? firestoreActionAuditUndoWriter).restoreField(
    event.targetCollection,
    event.targetDocumentID,
    change.key,
    oldValue,
    actionAuditUndoStamps(event.targetCollection, currentUser.id, timestamp),
  );

  const restoredData = applyActionAuditValue(currentData, change.key, oldValue);
  const context: ActionAuditContext = {
    leagueID: event.leagueID,
    organizationID: event.organizationID,
    subjectUserID: event.subjectUserID,
    targetCollection: event.targetCollection,
    targetDocumentID: event.targetDocumentID,
    targetDisplayName: event.targetDisplayName,
    actionType: "undo",
    actor: humanActionAuditActor(currentUser),
    fieldLabels: change.displayLabel ? { [change.key]: change.displayLabel } : undefined,
    reversesEventID: event.id,
    reversesFieldKey: change.key,
  };
  (dependencies.recorder ?? recordActionAudit)({ before: currentData, after: restoredData, context });
}
